import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, useSlotRecipe} from '@chakra-ui/react3'
import DisImage from '../../dis-image'
import PriceProduct from '../../cms/price-product'
import Link from '../../link'
import {navigationLinkRecipe} from './index.recipe'
import {isExternalLink} from '../../../../utils/utils'
import {hasValidImageSrc} from '../../../utils/utils'
import {convertToRelativePath, removeSiteLocaleFromPath} from '../../../../utils/url'
import RichText from '../../cms/rich-text'

/**
 * A navigation link supporting two visual variants: `link` and `card`.
 *
 * - `link` — a full-width link row with a `title` slot.
 * - `card` — a product card with an optional image, `copy`, and price derived from a product object.
 *
 * The type-gated slots read the authored `variant.type`, not the variant the recipe
 * resolved: an entry with no `variant` object takes the recipe's `link` styling but is
 * structurally a bare row — no card slots — which is what level 1 renders.
 *
 * Active state is controlled externally via the `isActive` prop; the parent component
 * is responsible for tracking which item is selected.
 * When clicking on the component, it navigates to the given link. If the link is within the Stokke
 * domain, it will use client-side SPA routing. If outside, it will open in a new tab/window.
 *
 * The variant and everything it carries arrive as the CMS `variant` object, so a
 * level-2 or level-3 entry is handed to the component as authored. A level-1 link
 * is the one entry the schema gives no such object; it falls back to the recipe
 * defaults and renders as a plain link row. A level-3 "More" link has no object
 * on the wire either, but its pane assembles one from the flat `accentColor` it
 * does carry, so it arrives here like any other link.
 *
 * @param {Object} [variant] - The CMS variant object
 * @param {'link'|'card'} [variant.type='link'] - Visual variant
 * @param {boolean|string} [variant.accentColor] - `link` only; renders the title in the brand orange token
 * @param {Object} [variant.image] - `card` only; the card thumbnail (3.75rem × 3.75rem). Its
 *   `src` arrives as a resolved URL — the authored `dynamicimage::` token is substituted before
 *   the payload reaches the client — and stays empty when no image was authored.
 * @param {string} [variant.copy] - `card` only; body copy shown below the title
 * @param {Object} [variant.priceProduct] - `card` only; the price, resolved from its `productId` by SFCC
 * @param {string} title - Primary title text
 * @param {boolean} [isActive] - Active/selected state, controlled by the parent
 * @param {Object} [link] - The CMS link object; its `value` is the target URL, defaulting to `/`
 * @param {Function} [onClick] - Optional additional click handler
 */
const NavigationLink = ({variant, title, isActive, link: {value: link = '/'} = {}, onClick}) => {
    const {type, accentColor, image, copy, priceProduct} = variant || {}
    const isCard = type === 'card'

    const recipe = useSlotRecipe({recipe: navigationLinkRecipe})
    const styles = useMemo(() => recipe({variant: type, accentColor}), [recipe, type, accentColor])

    const processedLink = useMemo(() => {
        const relative = convertToRelativePath(link)
        return isExternalLink(relative) ? relative : removeSiteLocaleFromPath(relative)
    }, [link])

    const isExternal = useMemo(() => isExternalLink(processedLink), [processedLink])

    const priceElement = useMemo(() => {
        // SFCC resolves `value` whenever a `productId` was authored, so an
        // absent one means the card authored no price at all. Dropping the row
        // here rather than leaving it to `PriceProduct`'s own null check keeps
        // the empty slot out of the card's grid.
        if (priceProduct?.value == null) return null
        return (
            <Box css={styles.price}>
                <PriceProduct {...priceProduct} variant="compact" />
            </Box>
        )
    }, [priceProduct, styles.price])

    return (
        <Link
            className="group"
            css={styles.root}
            to={processedLink}
            isExternal={isExternal}
            onClick={onClick}
            aria-current={isActive ? 'page' : undefined}
        >
            {isCard && (
                <Box css={styles.imageContainer}>
                    {hasValidImageSrc(image) && (
                        <DisImage
                            src={image.src}
                            // The navigation image carries no alternative text of its
                            // own in the schema, and the title beside it already names
                            // the entry, so it is decorative either way.
                            alt=""
                            objectFit="contain"
                            widths={{base: '60px'}} // matches imageContainer slot width of 3.75rem
                            // one pane serves every level-1 entry, so a new URL here is a different image (STOK-17559)
                            resetOnSrcChange
                        />
                    )}
                </Box>
            )}
            <Box css={styles.content}>
                <RichText css={styles.title}>{title}</RichText>
                {isCard && (
                    <>
                        {copy && <RichText css={styles.copy}>{copy}</RichText>}
                        {priceElement}
                    </>
                )}
            </Box>
        </Link>
    )
}

NavigationLink.propTypes = {
    variant: PropTypes.shape({
        type: PropTypes.oneOf(Object.keys(navigationLinkRecipe.variants.variant)),
        // Boolean and string forms both reach the client, and a recipe variant
        // is looked up by string key, so both select the same styles.
        accentColor: PropTypes.oneOf([true, false, 'true', 'false']),
        image: PropTypes.shape({
            src: PropTypes.string
        }),
        copy: PropTypes.string,
        // The authored `productId`/`label`, plus the four fields SFCC's
        // `calculatePriceProduct` resolves from the product.
        priceProduct: PropTypes.shape({
            productId: PropTypes.string,
            label: PropTypes.string,
            value: PropTypes.number,
            valueListPrice: PropTypes.number,
            hidePromotionSignals: PropTypes.bool,
            currency: PropTypes.string
        })
    }),
    title: PropTypes.string.isRequired,
    isActive: PropTypes.bool,
    link: PropTypes.shape({
        type: PropTypes.string,
        value: PropTypes.string
    }),
    onClick: PropTypes.func
}

export default NavigationLink
